import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Collapse, Modal } from 'react-bootstrap';

import './workday-certifications.css';

import EnumSelectDropdown, { EnumSelectDropdownHandle } from '../Filters/EnumSelectDropdown';
import SearchableStringListDropdown, { SearchableStringListDropdownHandle } from '../Filters/SearchableStringListDropdown';
import WordSearchInput, { WordSearch, WordSearchInputHandle } from '../Filters/WordSearchInput';

import ChevronContract from '../Icons/Chevron/ChevronContract';
import ChevronDown from '../Icons/Chevron/ChevronDown';
import ChevronExpand from '../Icons/Chevron/ChevronExpand';
import ResetFilter from '../Icons/ResetFilter';

import { ActiveInactiveStatus } from '../../enums/ActiveInactiveStatus';
import { CourseSortField } from '../../enums/CourseSort';
import { Sort } from '../../enums/Sort';

import { normalizeText } from '../../functions/TextHelpers';

import { linkTopicToCertification, unlinkTopicFromCertification } from '../../services/Workday/WorkdayCertificationService';
import { errorToast, infoToast, successToast } from '../Toasty';

import { WorkdayCertification } from '../../types/Workday/WorkdayCertification';
import { WorkdayCourse, WorkdayTopic } from '../../types/Workday/WorkdayTopic';


const DEFAULT_NAME_FILTER: WordSearch = { value: '', regex: undefined, sort: Sort.Ascending };

type WorkdayCertificationsTableProps = {
    certList: WorkdayCertification[];
    allTopics: WorkdayTopic[];
    userEmail?: string;
    canEdit?: boolean;
    isMocked?: boolean;
    onSaveSuccess?: () => Promise<void>;
};

const WorkdayCertificationsTable = ({ certList, allTopics, userEmail = '', canEdit = false, isMocked = false, onSaveSuccess }: WorkdayCertificationsTableProps) => {
    const [filteredList, setFilteredList] = useState<WorkdayCertification[]>(certList);
    const [expandedKeys, setExpandedKeys] = useState<Set<number>>(new Set());

    const [nameFilter, setNameFilter] = useState<WordSearch>(DEFAULT_NAME_FILTER);
    const [refIdFilter, setRefIdFilter] = useState<RegExp | undefined>();
    const [statusFilter, setStatusFilter] = useState<ActiveInactiveStatus | undefined>();
    const [courseIdFilter, setCourseIdFilter] = useState<string | undefined>();
    const [topicNameFilter, setTopicNameFilter] = useState<RegExp | undefined>();
    const [topicDescFilter, setTopicDescFilter] = useState<RegExp | undefined>();
    const [topicStatusFilter, setTopicStatusFilter] = useState<ActiveInactiveStatus | undefined>();
    const [courseSortField, setCourseSortField] = useState<CourseSortField>(CourseSortField.CourseName);

    const nameRef = useRef<WordSearchInputHandle>(null);
    const refIdRef = useRef<WordSearchInputHandle>(null);
    const statusRef = useRef<EnumSelectDropdownHandle>(null);
    const courseIdRef = useRef<SearchableStringListDropdownHandle>(null);
    const topicNameRef = useRef<WordSearchInputHandle>(null);
    const topicDescRef = useRef<WordSearchInputHandle>(null);
    const topicStatusRef = useRef<EnumSelectDropdownHandle>(null);

    const [expandedTopics, setExpandedTopics] = useState<Set<string>>(new Set());

    const [showFilters, setShowFilters] = useState(false);

    const [saving, setSaving] = useState(false);
    const [pickerCert, setPickerCert] = useState<WorkdayCertification | null>(null);
    const [pickerExpandedTopics, setPickerExpandedTopics] = useState<Set<number>>(new Set());
    const [pickerPendingIds, setPickerPendingIds] = useState<Set<number>>(new Set());
    const [pickerPendingRemoveIds, setPickerPendingRemoveIds] = useState<Set<number>>(new Set());
    const [pickerNameFilter, setPickerNameFilter] = useState<RegExp | undefined>();
    const [pickerCourseIdFilter, setPickerCourseIdFilter] = useState<string | undefined>();

    const pickerNameRef = useRef<WordSearchInputHandle>(null);
    const pickerCourseIdRef = useRef<SearchableStringListDropdownHandle>(null);

    const allCourseIds = useMemo(() => {
        const ids = new Set<string>();
        allTopics.forEach(t => t.courses.forEach(c => ids.add(c.workdayLearningCourseId)));
        return Array.from(ids).sort();
    }, [allTopics]);

    const certCourseIds = useMemo(() => {
        const ids = new Set<string>();
        certList.forEach(cert =>
            cert.topics.forEach(t =>
                t.courses.forEach(c => ids.add(c.workdayLearningCourseId))
            )
        );
        return Array.from(ids).sort();
    }, [certList]);

    useEffect(() => {
        if (!certList) return;

        const hasNameSort = nameFilter?.sort !== undefined;

        const filtered = certList.filter(c => {
            const name = normalizeText(c.name);
            const refId = normalizeText(c.workdayReferenceId);

            const matchesName = nameFilter?.regex ? nameFilter.regex.test(name) : true;
            const matchesRefId = refIdFilter ? refIdFilter.test(refId) : true;
            const matchesStatus = statusFilter !== undefined
                ? c.isActive === (statusFilter === ActiveInactiveStatus.Active)
                : true;
            const matchesCourseId = courseIdFilter
                ? c.topics.some(t => t.courses.some(course => course.workdayLearningCourseId === courseIdFilter))
                : true;
            const matchesTopicName = topicNameFilter
                ? c.topics.some(t => topicNameFilter.test(normalizeText(t.name)))
                : true;
            const matchesTopicDesc = topicDescFilter
                ? c.topics.some(t => topicDescFilter.test(normalizeText(t.description ?? '')))
                : true;
            const matchesTopicStatus = topicStatusFilter !== undefined
                ? c.topics.some(t => t.status === (topicStatusFilter === ActiveInactiveStatus.Active))
                : true;

            return matchesName && matchesRefId && matchesStatus && matchesCourseId
                && matchesTopicName && matchesTopicDesc && matchesTopicStatus;
        });

        const sorted = [...filtered].sort((a, b) => {
            const nameA = normalizeText(a.name);
            const nameB = normalizeText(b.name);
            if (hasNameSort) {
                const cmp = nameA.localeCompare(nameB);
                return nameFilter.sort === Sort.Ascending ? cmp : -cmp;
            }
            return nameA.localeCompare(nameB);
        });

        setFilteredList(sorted);
    }, [certList, nameFilter, refIdFilter, statusFilter, courseIdFilter, topicNameFilter, topicDescFilter, topicStatusFilter]);

    /**
     * Orders the courses shown under a topic in the main table by the field the user
     * picked in the Course Sort dropdown.
     *
     * @param courses - The topic's courses, in the order the API returned them.
     * @returns A sorted copy; the input is left untouched.
     */
    const sortCourses = (courses: WorkdayCourse[]): WorkdayCourse[] => (
        [...courses].sort((a, b) => (
            courseSortField === CourseSortField.CourseId
                ? a.workdayLearningCourseId.localeCompare(b.workdayLearningCourseId)
                : normalizeText(a.name).localeCompare(normalizeText(b.name))
        ))
    );

    const toggleExpand = (id: number) => {
        setExpandedKeys(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const expandAll = () => setExpandedKeys(new Set(certList.map(c => c.id)));
    const collapseAll = () => setExpandedKeys(new Set());

    const toggleTopicExpand = (certId: number, topicId: number) => {
        const key = `${certId}-${topicId}`;
        setExpandedTopics(prev => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });
    };

    const resetFilters = () => {
        nameRef.current?.clear();
        refIdRef.current?.clear();
        statusRef.current?.clear();
        courseIdRef.current?.clear();
        topicNameRef.current?.clear();
        topicDescRef.current?.clear();
        topicStatusRef.current?.clear();
    };

    const openPicker = (cert: WorkdayCertification) => {
        setPickerCert(cert);
        setPickerExpandedTopics(new Set());
        setPickerPendingIds(new Set());
        setPickerPendingRemoveIds(new Set());
        setPickerNameFilter(undefined);
        setPickerCourseIdFilter(undefined);
        pickerNameRef.current?.clear();
        pickerCourseIdRef.current?.clear();
    };

    const togglePickerExpand = (topicId: number) => {
        setPickerExpandedTopics(prev => {
            const next = new Set(prev);
            if (next.has(topicId)) next.delete(topicId);
            else next.add(topicId);
            return next;
        });
    };

    const togglePending = (topicId: number) => {
        setPickerPendingIds(prev => {
            const next = new Set(prev);
            if (next.has(topicId)) next.delete(topicId);
            else next.add(topicId);
            return next;
        });
    };

    const handleSavePicker = async () => {
        if (isMocked) {
            infoToast('You have clicked on the "Save" button.  This has been disabled because you are currently not yourself.');
            return;
        }
        if (!pickerCert || (pickerPendingIds.size === 0 && pickerPendingRemoveIds.size === 0)) return;

        setSaving(true);
        try {
            const [addResults, removeResults] = await Promise.all([
                Promise.all(
                    Array.from(pickerPendingIds).map(topicId =>
                        linkTopicToCertification(pickerCert.id, topicId, userEmail)
                    )
                ),
                Promise.all(
                    Array.from(pickerPendingRemoveIds).map(topicId =>
                        unlinkTopicFromCertification(pickerCert.id, topicId, userEmail)
                    )
                ),
            ]);

            const failedAdds = addResults.filter(r => r.status < 200 || r.status >= 300);
            const failedRemoves = removeResults.filter(r => r.status < 200 || r.status >= 300);

            if (failedAdds.length > 0 || failedRemoves.length > 0) {
                const parts: string[] = [];
                if (failedAdds.length > 0) parts.push(`${failedAdds.length} of ${addResults.length} topic(s) failed to add`);
                if (failedRemoves.length > 0) parts.push(`${failedRemoves.length} of ${removeResults.length} topic(s) failed to remove`);
                const detail = [...failedAdds, ...failedRemoves].map(r =>
                    `HTTP ${r.status || 'error'}${r.statusText ? ': ' + r.statusText : ''}${r.data?.message ? '\n' + r.data.message : r.data && typeof r.data === 'string' ? '\n' + r.data : ''}`
                ).join('\n');
                errorToast(parts.join('; '), { detail });
            } else {
                const parts: string[] = [];
                if (pickerPendingIds.size > 0) parts.push(`${pickerPendingIds.size} topic(s) added`);
                if (pickerPendingRemoveIds.size > 0) parts.push(`${pickerPendingRemoveIds.size} topic(s) removed`);
                successToast(parts.join(', ') + ' successfully');
                setPickerCert(null);
                if (onSaveSuccess) await onSaveSuccess();
            }
        } catch {
            errorToast('An unexpected error occurred while saving topics.');
        } finally {
            setSaving(false);
        }
    };

    const renderRows = () => (
        filteredList.map((c, i) => {
            const isExpanded = expandedKeys.has(c.id);
            const isOdd = i % 2 === 0;
            const rowClass = isOdd ? 'wc-row-odd' : 'wc-row-even';

            return (
                <React.Fragment key={c.id}>
                    <tr
                        data-id={c.id}
                        className={`align-middle cursor-pointer ${rowClass}`}
                        tabIndex={0}
                        aria-expanded={isExpanded}
                        aria-controls={`wc-expand-${c.id}`}
                        style={{ userSelect: 'none' }}
                        onClick={() => toggleExpand(c.id)}
                        onKeyDown={e => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                toggleExpand(c.id);
                            }
                        }}
                    >
                        <td>{c.name}</td>
                        <td>{c.workdayReferenceId}</td>
                        <td>{c.isActive ? 'Active' : 'Inactive'}</td>
                        <td className='text-end'>
                            <button
                                type='button'
                                title='Show topics'
                                className='btn p-0 border-0 bg-transparent'
                                aria-expanded={isExpanded}
                                aria-controls={`wc-expand-${c.id}`}
                                onClick={e => { e.stopPropagation(); toggleExpand(c.id); }}
                            >
                                <ChevronDown
                                    className='transition-chevron'
                                    style={{ transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)' }}
                                />
                            </button>
                        </td>
                    </tr>

                    <tr className={`wc-expanded-row ${rowClass}`}>
                        <td colSpan={4} className='p-0'>
                            <Collapse in={isExpanded}>
                                <div id={`wc-expand-${c.id}`} className={`border-top px-3 py-2 ${rowClass}`}>
                                    <div className='d-flex justify-content-between align-items-center mb-2'>
                                        <span className='text-muted small'>Associated Topics</span>
                                        {canEdit && (
                                            <button
                                                type='button'
                                                className='btn btn-sm btn-outline-primary'
                                                onClick={e => { e.stopPropagation(); openPicker(c); }}
                                            >
                                                Edit Topics
                                            </button>
                                        )}
                                    </div>
                                    {c.topics.length > 0 ? (
                                        <table className='wc-topic-table'>
                                            <colgroup>
                                                <col /><col /><col /><col />
                                            </colgroup>
                                            <thead>
                                                <tr>
                                                    <th>Topic Name</th>
                                                    <th>Description</th>
                                                    <th>Status</th>
                                                    <th></th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {c.topics.map((t, ti) => {
                                                    const topicKey = `${c.id}-${t.id}`;
                                                    const isTopicExpanded = expandedTopics.has(topicKey);
                                                    const topicRowClass = ti % 2 === 0 ? 'wc-topic-even' : 'wc-topic-odd';
                                                    return (
                                                        <React.Fragment key={t.id}>
                                                            <tr
                                                                className={`cursor-pointer ${topicRowClass}`}
                                                                onClick={e => { e.stopPropagation(); toggleTopicExpand(c.id, t.id); }}
                                                                style={{ userSelect: 'none' }}
                                                            >
                                                                <td>{t.name}</td>
                                                                <td>{t.description}</td>
                                                                <td>{t.status == null ? '—' : t.status ? 'Active' : 'Inactive'}</td>
                                                                <td className='text-end'>
                                                                    {t.courses.length > 0 && (
                                                                        <button
                                                                            type='button'
                                                                            title={isTopicExpanded ? 'Hide courses' : 'Show courses'}
                                                                            className='btn p-0 border-0 bg-transparent'
                                                                            onClick={e => { e.stopPropagation(); toggleTopicExpand(c.id, t.id); }}
                                                                        >
                                                                            <ChevronDown
                                                                                className='transition-chevron'
                                                                                style={{ transform: isTopicExpanded ? 'rotate(90deg)' : 'rotate(0deg)' }}
                                                                            />
                                                                        </button>
                                                                    )}
                                                                </td>
                                                            </tr>
                                                            <tr className={`wc-topic-expanded-row ${topicRowClass}`}>
                                                                <td colSpan={4} className='p-0'>
                                                                    <Collapse in={isTopicExpanded}>
                                                                        <div className='px-3 py-2 border-top'>
                                                                            <div className='d-flex align-items-center mb-2'>
                                                                                <span className='text-muted small'>Associated Courses</span>
                                                                            </div>
                                                                            {t.courses.length > 0 ? (
                                                                                <table className='wc-course-table'>
                                                                                    <colgroup>
                                                                                        <col /><col /><col />
                                                                                    </colgroup>
                                                                                    <thead>
                                                                                        <tr>
                                                                                            <th>Course Name</th>
                                                                                            <th>Course ID</th>
                                                                                            <th>Description</th>
                                                                                        </tr>
                                                                                    </thead>
                                                                                    <tbody>
                                                                                        {sortCourses(t.courses).map((course, ci) => (
                                                                                            <tr key={course.id} className={ci % 2 === 0 ? 'wc-course-even' : 'wc-course-odd'}>
                                                                                                <td>{course.name}</td>
                                                                                                <td>{course.workdayLearningCourseId}</td>
                                                                                                <td>{course.description ?? '—'}</td>
                                                                                            </tr>
                                                                                        ))}
                                                                                    </tbody>
                                                                                </table>
                                                                            ) : (
                                                                                <div className='text-muted small fst-italic'>No courses linked to this topic.</div>
                                                                            )}
                                                                        </div>
                                                                    </Collapse>
                                                                </td>
                                                            </tr>
                                                        </React.Fragment>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    ) : (
                                        <div className='text-muted small fst-italic'>
                                            {canEdit ? 'No topics linked. Click "Edit Topics" to get started.' : 'No topics linked.'}
                                        </div>
                                    )}
                                </div>
                            </Collapse>
                        </td>
                    </tr>
                </React.Fragment>
            );
        })
    );

    const renderNoList = () => (
        <tr>
            <td className='px-2'><div className='text-muted'>None</div></td>
            <td className='px-2'><div className='text-muted'>None</div></td>
            <td className='px-2'><div className='text-muted'>None</div></td>
            <td className='px-2'></td>
        </tr>
    );

    const renderTopicPickerModal = () => {
        if (!pickerCert) return null;

        const linkedTopicIds = new Set(pickerCert.topics.map(t => t.id));
        const canSave = pickerPendingIds.size > 0 || pickerPendingRemoveIds.size > 0;

        /**
         * A topic can only be linked if it is active and actually carries courses — an empty or
         * retired topic grants nothing. Topics already linked to this certification are exempt:
         * one that has since gone inactive or lost its courses still has to be visible here, or
         * there would be no way to unlink it.
         */
        const isSelectable = (t: WorkdayTopic) => t.status === true && t.courses.length > 0;

        const pickerTopics = allTopics.filter(t => {
            const matchesName = pickerNameFilter
                ? pickerNameFilter.test(normalizeText(t.name))
                : true;
            const matchesCourseId = pickerCourseIdFilter
                ? t.courses.some(c => c.workdayLearningCourseId === pickerCourseIdFilter)
                : true;
            return (isSelectable(t) || linkedTopicIds.has(t.id)) && matchesName && matchesCourseId;
        });

        return (
            <Modal
                show
                onHide={() => setPickerCert(null)}
                size='xl'
                dialogClassName='wc-picker-dialog'
                // the Course ID menu is portalled to document.body, so the default focus trap
                // pulls focus off its search input and swallows every keystroke
                enforceFocus={false}
            >
                <Modal.Header closeButton>
                    <Modal.Title className='w-100 text-center'>Edit Topics — {pickerCert.name}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className='row g-2 mb-3'>
                        <div className='col'>
                            <WordSearchInput
                                label='Topic Name'
                                ref={pickerNameRef}
                                filterFillerWords={false}
                                onChange={({ regex }) => setPickerNameFilter(regex)}
                                placeholder='Filter topics...'
                            />
                        </div>
                        <div className='col'>
                            <SearchableStringListDropdown
                                label='Course ID'
                                ref={pickerCourseIdRef}
                                options={allCourseIds}
                                placeholder='Filter by course ID...'
                                clearable
                                onChange={v => setPickerCourseIdFilter(v)}
                            />
                        </div>
                    </div>
                    <table className='wc-picker-table'>
                        <colgroup>
                            <col /><col /><col />
                        </colgroup>
                        <thead>
                            <tr>
                                <th>Topic Name</th>
                                <th>Status</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {pickerTopics.map((t, ti) => {
                                const isLinked = linkedTopicIds.has(t.id);
                                const isPending = pickerPendingIds.has(t.id);
                                const isPendingRemove = pickerPendingRemoveIds.has(t.id);
                                const isPickerExpanded = pickerExpandedTopics.has(t.id);
                                const rowClass = ti % 2 === 0 ? 'wc-picker-even' : 'wc-picker-odd';

                                const togglePendingRemove = (id: number) => {
                                    setPickerPendingRemoveIds(prev => {
                                        const next = new Set(prev);
                                        if (next.has(id)) next.delete(id);
                                        else next.add(id);
                                        return next;
                                    });
                                };

                                return (
                                    <React.Fragment key={t.id}>
                                        <tr className={rowClass}>
                                            <td title={t.description || 'No Description Provided'}>{t.name}</td>
                                            <td>{t.status == null ? '—' : t.status ? 'Active' : 'Inactive'}</td>
                                            <td>
                                                <div className='d-flex gap-1 justify-content-end'>
                                                    {t.courses.length > 0 && (
                                                        <button
                                                            type='button'
                                                            title={isPickerExpanded ? 'Hide courses' : 'Show courses'}
                                                            className='btn btn-sm btn-outline-secondary p-0 px-1'
                                                            onClick={() => togglePickerExpand(t.id)}
                                                        >
                                                            <ChevronDown
                                                                className='transition-chevron'
                                                                style={{ transform: isPickerExpanded ? 'rotate(90deg)' : 'rotate(0deg)' }}
                                                            />
                                                        </button>
                                                    )}
                                                    {isLinked ? (
                                                        <button
                                                            type='button'
                                                            className={`btn btn-sm p-0 px-2 wc-picker-btn ${isPendingRemove ? 'btn-warning' : 'btn-outline-danger'}`}
                                                            title={isPendingRemove ? 'Click to keep this topic' : 'Remove this topic from the certification'}
                                                            onClick={() => togglePendingRemove(t.id)}
                                                        >
                                                            {isPendingRemove ? 'Undo' : 'Remove'}
                                                        </button>
                                                    ) : (
                                                        <button
                                                            type='button'
                                                            className={`btn btn-sm p-0 px-2 wc-picker-btn ${isPending ? 'btn-primary' : 'btn-outline-primary'}`}
                                                            title={isPending ? 'Click to remove from selection' : 'Add to selection'}
                                                            onClick={() => togglePending(t.id)}
                                                        >
                                                            {isPending ? '✓ Selected' : '+ Add'}
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>

                                        <tr className={`wc-picker-expand-row ${rowClass}`}>
                                            <td colSpan={3} className='p-0'>
                                                <Collapse in={isPickerExpanded}>
                                                    <div className='px-3 py-1 border-top'>
                                                        <table className='wc-picker-course-table'>
                                                            <colgroup>
                                                                <col /><col />
                                                            </colgroup>
                                                            <thead>
                                                                <tr>
                                                                    <th>Course Name</th>
                                                                    <th>Course ID</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {t.courses.map((course, ci) => (
                                                                    <tr key={course.id} className={ci % 2 === 0 ? 'wc-picker-course-even' : 'wc-picker-course-odd'}>
                                                                        <td title={course.description || 'No Description Provided'}>{course.name}</td>
                                                                        <td>{course.workdayLearningCourseId}</td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                </Collapse>
                                            </td>
                                        </tr>
                                    </React.Fragment>
                                );
                            })}
                        </tbody>
                    </table>
                </Modal.Body>
                <Modal.Footer>
                    <span className='text-muted small me-auto'>
                        {pickerPendingIds.size > 0 && `${pickerPendingIds.size} to add`}
                        {pickerPendingIds.size > 0 && pickerPendingRemoveIds.size > 0 && ', '}
                        {pickerPendingRemoveIds.size > 0 && `${pickerPendingRemoveIds.size} to remove`}
                        {pickerPendingIds.size === 0 && pickerPendingRemoveIds.size === 0 && 'No changes'}
                    </span>
                    <button className='btn btn-outline-secondary' disabled={saving} onClick={() => setPickerCert(null)}>
                        Cancel
                    </button>
                    <button className='btn btn-primary' disabled={!canSave || saving} onClick={handleSavePicker}>
                        {saving ? 'Saving…' : 'Save'}
                    </button>
                </Modal.Footer>
            </Modal>
        );
    };

    return (
        <div className='border rounded-2 shadow-sm workday-certifications'>
            {renderTopicPickerModal()}
            <div className='wc-controls-bar border-bottom'>
                <div className='wc-controls-header'>
                    <div className='d-flex gap-1 flex-shrink-0'>
                        <button
                            className='btn btn-sm btn-outline-secondary'
                            title={showFilters ? 'Fewer filters' : 'More filters'}
                            onClick={() => setShowFilters(f => !f)}
                        >
                            <ChevronDown
                                className='transition-chevron'
                                style={{ transform: showFilters ? 'rotate(0deg)' : 'rotate(-90deg)' }}
                            />
                            {' '}Filters
                        </button>
                        <button
                            title='Reset filters'
                            className='btn btn-sm btn-primary'
                            onClick={resetFilters}
                        >
                            <ResetFilter />
                        </button>
                    </div>
                    <div className='wc-filter-primary'>
                        <div>
                            <WordSearchInput
                                label='Name'
                                sortable
                                sort={nameFilter.sort}
                                filterFillerWords={false}
                                ref={nameRef}
                                onChange={(filter: WordSearch) => setNameFilter({
                                    ...filter,
                                    sort: filter.sort === undefined ? Sort.Ascending : filter.sort,
                                })}
                                placeholder='Filter names...'
                            />
                        </div>
                        <div>
                            <WordSearchInput
                                label='Reference ID'
                                ref={refIdRef}
                                filterFillerWords={false}
                                onChange={({ regex }) => setRefIdFilter(regex)}
                                placeholder='Filter reference ID...'
                            />
                        </div>
                        <div>
                            <EnumSelectDropdown
                                label='Active'
                                ref={statusRef}
                                enumObj={ActiveInactiveStatus}
                                placeholder='Filter...'
                                onChange={s => setStatusFilter(s)}
                                clearable
                            />
                        </div>
                    </div>
                    <div className='d-flex gap-1 flex-shrink-0'>
                        <button
                            className='btn btn-sm btn-primary'
                            title={expandedKeys.size > 0 ? 'Collapse all' : 'Expand all'}
                            onClick={() => expandedKeys.size > 0 ? collapseAll() : expandAll()}
                        >
                            {expandedKeys.size > 0 ? <ChevronContract /> : <ChevronExpand />}
                        </button>
                    </div>
                </div>
                <Collapse in={showFilters}>
                    <div>
                        <div className='wc-filter-middle'>
                            <div>
                                <WordSearchInput
                                    label='Topic Name'
                                    ref={topicNameRef}
                                    filterFillerWords={false}
                                    wrapperClassName='mb-2'
                                    onChange={({ regex }) => setTopicNameFilter(regex)}
                                    placeholder='Filter by topic name...'
                                />
                            </div>
                            <div>
                                <WordSearchInput
                                    label='Topic Description'
                                    ref={topicDescRef}
                                    filterFillerWords={false}
                                    wrapperClassName='mb-2'
                                    onChange={({ regex }) => setTopicDescFilter(regex)}
                                    placeholder='Filter by topic description...'
                                />
                            </div>
                            <div>
                                <EnumSelectDropdown
                                    label='Topic Status'
                                    ref={topicStatusRef}
                                    wrapperClassName='mb-2'
                                    enumObj={ActiveInactiveStatus}
                                    placeholder='Filter...'
                                    onChange={s => setTopicStatusFilter(s)}
                                    clearable
                                />
                            </div>
                        </div>
                        <div className='wc-filter-secondary'>
                            <div>
                                <SearchableStringListDropdown
                                    label='Course ID'
                                    ref={courseIdRef}
                                    options={certCourseIds}
                                    placeholder='Filter by course ID...'
                                    clearable
                                    wrapperClassName='mb-2'
                                    onChange={v => setCourseIdFilter(v)}
                                />
                            </div>
                            <div>
                                <EnumSelectDropdown
                                    label='Course Sort'
                                    wrapperClassName='mb-2'
                                    enumObj={CourseSortField}
                                    defaultValue={CourseSortField.CourseName}
                                    labels={{ CourseName: 'Course Name', CourseId: 'Course ID' }}
                                    clearable={false}
                                    onChange={f => setCourseSortField(f ?? CourseSortField.CourseName)}
                                />
                            </div>
                        </div>
                    </div>
                </Collapse>
            </div>
            <div className='wc-table-container'>
                <table className='wc-table my-2'>
                    <colgroup>
                        <col /><col /><col /><col />
                    </colgroup>
                    <thead>
                        <tr>
                            <th className='wc-header'>Name</th>
                            <th className='wc-header'>Reference ID</th>
                            <th className='wc-header'>Status</th>
                            <th className='wc-header'></th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredList.length > 0 ? renderRows() : renderNoList()}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default WorkdayCertificationsTable;
